import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  verifyPasswordResetCode,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  verifyBeforeUpdateEmail,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  applyActionCode,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { generateUniquePath } from './user';
import type { SignUpData } from '../auth/validation';
import { toast } from 'sonner';
import { functionsUrl } from './functionsUrl';
import { getStoredUtms } from '../utm';
import { trackSignup } from '../utils/analytics';

export type UserType = 'member' | 'creator';
export type UserRoles = {
  member: boolean;
  creator: boolean;
};

export async function signUpWithEmail(data: SignUpData & { creatorId?: string }) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);

    const fullName = `${data.firstName} ${data.lastName}`;
    await updateProfile(userCredential.user, { displayName: fullName });

    // Trigger our server to send verification via Resend (improves deliverability)
    try {
      await fetch(functionsUrl('sendVerificationEmail'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, displayName: fullName, creatorId: data.creatorId }),
      });
    } catch { }

    // Create user document with member role by default
    const defaultPath = await generateUniquePath(data.firstName, data.lastName, userCredential.user.uid);
    // Detect environment from Stripe key (test keys start with pk_test_)
    const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
    const environment = stripeKey.startsWith('pk_test_') ? 'development' : 'production';

    const utms = getStoredUtms();

    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      displayName: fullName,
      // Default membership/engagement settings
      allowFreeFollowing: true,
      allowEngagement: true,
      path: defaultPath,
      roles: {
        member: true,
        creator: false
      },
      environment, // Track which environment the user was created in
      utm: utms,
      howDidYouHear: data.howDidYouHear || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    trackSignup('email', utms, data.howDidYouHear);

    // Don't show toast here - let the calling component handle the UX
    return userCredential.user;
  } catch (error: any) {
    console.error('Error creating account:', error);
    const isDuplicateEmail = error?.code === 'auth/email-already-in-use' ||
      error?.message?.includes('auth/email-already-in-use');
    const friendlyMessage = isDuplicateEmail
      ? 'Email already registered. Please log in instead.'
      : error?.message || 'Failed to create account';

    const mappedError = new Error(friendlyMessage);
    if (error?.code) {
      (mappedError as any).code = error.code;
    }
    throw mappedError;
  }
}

export const signInWithEmail = async (email: string, password: string) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error: any) {
    console.error('Email sign in error:', error);
    throw new Error(error.message || 'Failed to sign in with email');
  }
};

export async function signInWithGoogle() {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const isNewUser = getAdditionalUserInfo(result)?.isNewUser === true;

    // Split the display name into first and last name
    const nameParts = result.user.displayName?.split(' ') || ['', ''];
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    // TSF-233. This used to write the whole signup document on EVERY Google
    // sign-in, with merge:true. Two things that costs a returning athlete:
    //
    //   path — generateUniquePath() was called without excludeUserId, so their
    //   OWN saved path counted as taken and they were handed `<name>-1`, then
    //   `<name>-2`... Every Google sign-in silently rotated the athlete's public
    //   fund URL out from under every link they had already shared. Measured on
    //   a real athlete: path `nuraldaim-100-1`, photoURL from Google.
    //
    //   roles — merge:true deep-merges maps, so `creator: false` was written
    //   over the role of anyone who already was a creator, demoting them.
    //
    // A returning user's document is already correct. Only create it when it is
    // genuinely missing, and otherwise touch nothing but the Google avatar.
    const userRef = doc(db, 'users', result.user.uid);
    const existing = await getDoc(userRef);

    if (!existing.exists()) {
      const defaultPathGoogle = await generateUniquePath(firstName, lastName, result.user.uid);
      // Detect environment from Stripe key (test keys start with pk_test_)
      const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
      const environment = stripeKey.startsWith('pk_test_') ? 'development' : 'production';
      const utms = getStoredUtms();

      await setDoc(userRef, {
        email: result.user.email,
        firstName,
        lastName,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        // Default membership/engagement settings
        allowFreeFollowing: true,
        allowEngagement: true,
        path: defaultPathGoogle,
        roles: {
          member: true,
          creator: false
        },
        environment, // Track which environment the user was created in
        ...(utms ? { utm: utms } : {}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } else if (result.user.photoURL && !(existing.data() as any)?.photoURL) {
      await setDoc(userRef, {
        photoURL: result.user.photoURL,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }

    if (isNewUser) {
      const signupUtms = getStoredUtms();
      if (signupUtms) trackSignup('google', signupUtms);
    }

    toast.success('Signed in successfully!');
    return result.user;
  } catch (error: any) {
    console.error('Error signing in with Google:', error);
    throw new Error(error.message || 'Failed to sign in with Google');
  }
}

export async function signOut() {
  try {
    await firebaseSignOut(auth);
    toast.success('Signed out successfully');
  } catch (error: any) {
    console.error('Error signing out:', error);
    throw new Error(error.message || 'Failed to sign out');
  }
}

export async function sendPasswordReset(email: string) {
  try {
    // Prefer backend sending via Resend so the from address is info@thesportfund.com
    const response = await fetch(functionsUrl('sendPasswordResetEmail'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      throw new Error('Backend password reset email failed');
    }
    toast.success('Password reset email sent');
  } catch (error: any) {
    console.error('Error sending password reset via backend, falling back to Firebase:', error);
    // Fallback to Firebase default email
    try {
      const actionCodeSettings = { url: `${window.location.origin}/password-reset`, handleCodeInApp: true } as const;
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      toast.success('Password reset email sent');
    } catch (inner: any) {
      console.error('Fallback password reset failed:', inner);
      throw new Error(inner.message || 'Failed to send password reset email');
    }
  }
}

export async function confirmPasswordReset(code: string, newPassword: string) {
  try {
    // Ensure the code is valid first for better error messages
    await verifyPasswordResetCode(auth, code);
    await firebaseConfirmPasswordReset(auth, code, newPassword);
    toast.success('Password reset successful');
  } catch (error: any) {
    console.error('Error resetting password:', error);
    throw new Error(error.message || 'Failed to reset password');
  }
}

export async function resendVerificationEmail() {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No user signed in');
    // Call backend to send via Resend from info@thesportfund.com
    const response = await fetch(functionsUrl('sendVerificationEmail'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: user.email, displayName: user.displayName }),
    });
    // TSF-323: never claim "sent" for a request that failed.
    if (!response.ok) throw new Error('Could not send the verification email. Please try again in a few minutes.');
    toast.success('Verification email sent');
  } catch (error: any) {
    console.error('Error sending verification email:', error);
    throw new Error(error.message || 'Failed to send verification email');
  }
}

export async function updateUserPassword(currentPassword: string, newPassword: string) {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('No user signed in');

    // Reauthenticate with the user's current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await firebaseUpdatePassword(user, newPassword);
    toast.success('Password updated successfully');
  } catch (error: any) {
    console.error('Error updating password:', error);
    throw new Error(error.message || 'Failed to update password');
  }
}

export async function updateUserEmail(currentPassword: string, newEmail: string) {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('No user signed in');

    if (newEmail === user.email) {
      toast.success('Email is unchanged');
      return;
    }

    // Reauthenticate first using current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Email enumeration protection is on for this project, so Firebase refuses
    // a direct updateEmail ("Please verify the new email before changing
    // email", auth/operation-not-allowed) for every user. The supported path:
    // mail a link to the NEW address; the auth email changes when it is
    // clicked, and AuthProvider copies it into users/{uid} on next sign-in.
    await verifyBeforeUpdateEmail(user, newEmail);

    toast.success(`Check ${newEmail} and click the link to confirm. Then sign in with your new email.`);
  } catch (error: any) {
    console.error('Error updating email:', error);
    throw new Error(error.message || 'Failed to update email');
  }
}

export async function deleteUserAccount() {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No user signed in');
    await user.delete();
    toast.success('Account deleted successfully');
  } catch (error: any) {
    console.error('Error deleting account:', error);
    throw new Error(error.message || 'Failed to delete account');
  }
}

export async function upgradeToCreator(userId: string) {
  try {
    await updateDoc(doc(db, 'users', userId), {
      roles: {
        member: true,
        creator: true
      },
      updatedAt: serverTimestamp(),
    });
    toast.success('Successfully upgraded to creator account!');
  } catch (error: any) {
    console.error('Error upgrading to creator:', error);
    throw new Error(error.message || 'Failed to upgrade to creator account');
  }
}

/**
 * Redeem a verifyEmail link's oobCode. The handler used to only CHECK currentUser.emailVerified
 * and never applied the code, so every verification link said "Invalid or expired" (found on
 * Devotion Fund 2026-10-05, same code here). A code is single-use: if it was already redeemed
 * (second click, mail scanner) and the account is verified, that is still a success.
 */
export async function applyEmailVerification(oobCode: string): Promise<'verified' | 'invalid'> {
  try {
    await applyActionCode(auth, oobCode);
    await auth.currentUser?.reload().catch(() => {});
    return 'verified';
  } catch (e: any) {
    console.error('applyActionCode failed:', e?.code || e);
    return (await isEmailVerified()) ? 'verified' : 'invalid';
  }
}

export async function isEmailVerified(): Promise<boolean> {
  try {
    const user = auth.currentUser;
    if (!user) return false;

    // Reload user to get latest email verification status
    await user.reload();
    return user.emailVerified;
  } catch (error: any) {
    console.error('Error checking email verification:', error);
    return false;
  }
}

// EmailSignIn.tsx has imported `sendSignInLink` since it was written, but the
// export never existed -- which is why /email-signin was never routed: wiring it
// up would have shipped a page that throws on submit. The handler at
// /auth/email-handler reads the address back out of localStorage
// ('emailForSignIn'), so storing it here is part of the contract, not a nicety.
export async function sendSignInLink(email: string) {
  await sendSignInLinkToEmail(auth, email, {
    url: `${window.location.origin}/auth/email-handler`,
    handleCodeInApp: true,
  });
  localStorage.setItem('emailForSignIn', email);
}

export function checkIfEmailLink(url: string): boolean {
  return isSignInWithEmailLink(auth, url);
}

export async function completeSignInWithLink(email: string, url: string) {
  const cred = await signInWithEmailLink(auth, email, url);
  return cred.user;
}

export async function sendCustomVerificationEmail(email: string, displayName?: string) {
  try {
    // First, send the Firebase verification email to get the verification link
    const user = auth.currentUser;
    if (!user) throw new Error('No user signed in');

    // Get the verification link from Firebase
    const actionCodeSettings = {
      url: `${window.location.origin}/auth/email-handler`,
      handleCodeInApp: true,
    };

    // This will generate the verification link
    const verificationLink = await sendEmailVerification(user, actionCodeSettings);

    // Now send a custom email using Resend
    const response = await fetch(functionsUrl('sendVerificationEmail'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        verificationLink,
        displayName: displayName || user.displayName || 'there'
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send custom verification email');
    }

    return true;
  } catch (error: any) {
    console.error('Error sending custom verification email:', error);
    throw new Error(error.message || 'Failed to send verification email');
  }
}